import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Link2, AlertTriangle } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PurchaseOrderCombobox, type PurchaseOrderPick } from '@/components/PurchaseOrderCombobox';
import { invalidateEstimatePoLinkCaches } from '@/lib/linkCache';

// ─── LinkPoToEstimateModal ──────────────────────────────────────────────────────
// Attach an EXISTING purchase order to this estimate. Used from the estimate detail
// page (and reusable from the estimate list). Confirming PATCHes the picked PO's
// estimate-link to this estimate. If the PO is already linked to a DIFFERENT
// estimate, a warning is shown before confirming — the link will be moved.

interface EstimateLinkResponse {
  data: {
    id: number;
    source_estimate_id: number | null;
    source_estimate_number: string | null;
  };
}

export interface LinkPoToEstimateModalProps {
  estimateId: number;
  estimateNumber: string;
  onClose: () => void;
  onLinked: () => void;
}

export function LinkPoToEstimateModal({
  estimateId,
  estimateNumber,
  onClose,
  onLinked,
}: LinkPoToEstimateModalProps) {
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<PurchaseOrderPick | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // The picked PO is already linked to a DIFFERENT estimate → confirming moves it.
  const movingFrom =
    picked && picked.source_estimate_id !== null && picked.source_estimate_id !== estimateId
      ? picked.source_estimate_number
      : null;

  // Already on THIS estimate → nothing to do.
  const alreadyHere = picked?.source_estimate_id === estimateId;

  async function handleConfirm() {
    if (!picked) return;
    setError('');
    setLoading(true);
    try {
      await apiFetch<EstimateLinkResponse>(`/api/purchase-orders/${picked.id}/estimate-link`, {
        method: 'PATCH',
        body: JSON.stringify({ source_estimate_id: estimateId }),
      });
      toast.success(`${picked.number} linked to ${estimateNumber}`);
      invalidateEstimatePoLinkCaches(queryClient);
      onLinked();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Link existing purchase order</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Attach an existing PO to estimate{' '}
            <span className="font-mono font-medium text-foreground">{estimateNumber}</span>.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="link-po-search">Purchase order</Label>
            <PurchaseOrderCombobox
              value={picked?.id ?? null}
              onChange={setPicked}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">Search by PO number or supplier name.</p>
          </div>
          {movingFrom && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {picked?.number} is already linked to{' '}
                <span className="font-mono font-medium">{movingFrom}</span> — linking here will move
                it.
              </span>
            </div>
          )}
          {alreadyHere && (
            <p className="text-sm text-muted-foreground">
              {picked?.number} is already linked to this estimate.
            </p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={loading || !picked || alreadyHere}
            >
              <Link2 className="h-3.5 w-3.5 mr-1.5" />
              {loading ? 'Linking…' : movingFrom ? 'Move & link' : 'Link'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
